<template>
  <div :class="{'eb-card': home}">
    <div v-if="!home" class="text-right md:flex md:justify-between md:align-items-center">
      <EbHeading v-if="!isMobile && !isSmallScreen" level="h1" class="eb-page-heading">Mes accueils</EbHeading>
      <a :href="publicProfileUrl" class="eb-button secondary small icon-after" target="_blank" rel="noreferrer noopener" title="Voir ma fiche en ligne - Nouvelle fenêtre">Voir ma fiche en ligne <font-awesome-icon :icon="['fas', 'external-link-alt']" /></a>	
    </div>
    
    <div :class="{'col-span-2': home}">
      <div class="eb-content-default">
        
        <EbBanner :isActive="isBannerActive" :type="bannerType" :title="bannerTitle" :timestamp="bannerTimestamp">{{bannerContent}}</EbBanner>
        
        <div class="flex flex-col md:flex-row md:justify-between md:align-items-start">
          <EbHeading v-if="home" level="h2">Mes accueils</EbHeading>
          <EbHeading v-else level="h2" class="eb-box-heading">Mes accueils en cours</EbHeading>
          <EbLinkButton v-if="enCours != 0" :to="{name: 'choixAccueil'}" :class="['primary small', 'mb-24', {'eb-card-button': home}]">Déclarer un accueil</EbLinkButton>
        </div>
        
        <div v-if="!home" class="eb-exergue eb-lighter-blue-bgcolor"><p>Départ ou changement dans l'accueil d'un enfant ? <a href="https://declaration-accueil.maine-et-loire.fr/" title="Utilisez l’ancien formulaire - Nouvelle fenêtre" target="_blank" rel="noreferrer noopener">Utilisez l’ancien formulaire</a> pour tout enfant accueilli avant novembre 2022.</p></div>
        
        <EbLinkButton v-if="home" :to="{name: 'mesAccueils'}" class="eb-card-button small text m-auto">Voir tous mes accueils</EbLinkButton>
        
        <EbHeading v-if="home" level="h3" class="eb-box-heading">Mes accueils en cours</EbHeading>
        <p v-if="!home">Retrouvez les déclarations d'accueil saisies en ligne. Pour un meilleur suivi, merci de privilégier ce mode de saisie.</p>
        
        <div v-if="!loading">
          <ul v-if="enCours && enCours.length > 0" class="eb-list">
            <li v-for="acc in enCours" class="eb-list-item">
              <span class="eb-list-item-content" :class="{w75: !home} && {w50: !isMobile}">
                <router-link :to="{name: 'ficheEnfant', params: {idEnfant: Number(acc.id)}}" class="eb-list-item-title" :title="getTitleLinkFiche(acc)">
                  <span class="sr-only">Voir la fiche de </span>
                  <span class="text-uppercase">{{ acc.nom }}</span> {{ acc.prenom }}
                </router-link>
                <span><i>Enfant accueilli depuis</i> le <b><time :datetime="acc.dateArrivee">{{ getLongDate(acc.dateArrivee) }}</time></b></span>
                <EbEtiquette v-if="home || isMobile" type="success" iconBefore lightBg>Déclaration modifiée le <time :datetime="acc.dateModification">{{ getLongDate(acc.dateModification) }}</time></EbEtiquette>
              </span>
              <EbEtiquette v-if="!home && !isMobile" type="success" iconBefore lightBg>Déclaration modifiée le <time :datetime="acc.dateModification">{{ getLongDate(acc.dateModification) }}</time></EbEtiquette>
              
              <div v-if="!isMobile" class="eb-list-buttons">
                <EbLinkButton :to="{name: 'ficheEnfantModify', params: {idEnfant: Number(acc.id)}}" :title="'Modifier la fiche de ' + acc.nom + ' ' + acc.prenom" class="text icon-only">
                  <font-awesome-icon :icon="['fas', 'pen']" size="lg" /><span class="sr-only">Modifier la fiche de {{ acc.nom }} {{ acc.prenom }}</span>
                </EbLinkButton>
                <EbLinkButton :to="{name: 'ficheEnfantDelete', params: {idEnfant: Number(acc.id)}}" :title="'Déclarer la fin de l’accueil de ' + acc.nom + ' ' + acc.prenom" class="text icon-only">
                  <font-awesome-icon :icon="['fas', 'door-open']" size="lg" /><span class="sr-only">Déclarer la fin de l’accueil de {{ acc.nom }} {{ acc.prenom }}</span>
                </EbLinkButton>
              </div>
              <EbLinkButton v-if="isMobile" :to="{name: 'ficheEnfantModify', params: {idEnfant: Number(acc.id)}}" :title="'Modifier la fiche de ' + acc.nom + ' ' + acc.prenom" class="text icon-only">
                <font-awesome-icon :icon="['fas', 'pen']" size="lg" /><span class="sr-only">Modifier la fiche de {{ acc.nom }} {{ acc.prenom }}</span>
              </EbLinkButton>
              <EbLinkButton v-if="isMobile" :to="{name: 'ficheEnfantDelete', params: {idEnfant: Number(acc.id)}}" :title="'Déclarer la fin de l’accueil de ' + acc.nom + ' ' + acc.prenom" class="text icon-only">
                <font-awesome-icon :icon="['fas', 'door-open']" size="lg" /><span class="sr-only">Déclarer la fin de l’accueil de {{ acc.nom }} {{ acc.prenom }}</span>
              </EbLinkButton>
            </li>
          </ul>
          
          <div v-else class="flex justify-center align-items-center flex-col text-center m-36">
            <font-awesome-icon :icon="['fas', 'house-user']" size="3x" class="m-24" />
            <p class="text-larger">Commencez par saisir une déclaration en ligne.</p>
            <p>Cliquez sur <b>Déclarer un nouvel accueil</b> pour créer une déclaration.</p>
            <EbLinkButton :to="{name: 'accueilDeclaration'}" class="primary medium">Déclarer un accueil</EbLinkButton>
          </div>
        </div>
        <div v-else>
          <EbLoader></EbLoader>
        </div>
        
        <EbHeading :level="!home ? 'h2': 'h3'" class="eb-box-heading">Mes accueils dérogatoires</EbHeading>
        <div v-if="!loading">
          <ul v-if="derogatoire && derogatoire.length > 0" class="eb-list">
            <li v-for="acc in derogatoire" class="eb-list-item">
              <span class="eb-list-item-content" :class="{w75: !home} && {w50: !isMobile}">
                
                <p class="eb-list-item-title" v-if="acc.accueilSupplementairePonctuel">
                  Accueil supplémentaire
                </p>
                <p class="eb-list-item-title" v-else>
                  Accueil en surnombre
                </p>
                
                <p v-if="acc.accueilSupplementairePonctuel">
                  <span class="text-uppercase">{{ acc.accueilSupplementairePonctuel.nomEnfant }}</span> {{ acc.accueilSupplementairePonctuel.prenomEnfant }}, depuis le <b><time :datetime="acc.dateDebut">{{ getLongDate(acc.dateDebut) }}</time></b>
                </p>
                <p v-else>
                  Date de début : <b><time :datetime="acc.dateDebut">{{ getLongDate(acc.dateDebut) }}</time></b>
                </p>
                <EbEtiquette v-if="home || isMobile" type="archive" lightBg>Déclaré le <time :datetime="acc.dateDeclaration">{{ getLongDate(acc.dateDeclaration) }}</time></EbEtiquette>
              </span>
              
              <EbEtiquette v-if="!home && !isMobile" type="archive" lightBg>Déclaré le <time :datetime="acc.dateDeclaration">{{ getLongDate(acc.dateDeclaration) }}</time></EbEtiquette>
              
              <div v-if="!isMobile" class="eb-list-buttons">
                <EbLinkButton :to="{name: 'ficheAccueilDerogatoire', params: {idAccueil: Number(acc.id)}}" title="Supprimer l’accueil dérogatoire" class="text icon-only">
                  <font-awesome-icon :icon="['fas', 'trash']" size="lg" /><span class="sr-only">Supprimer l’accueil dérogatoire</span>
                </EbLinkButton>
              </div>
              <EbLinkButton v-else :to="{name: 'ficheAccueilDerogatoire', params: {idAccueil: Number(acc.id)}}" title="Supprimer l’accueil dérogatoire" class="text icon-only">
                <font-awesome-icon :icon="['fas', 'trash']" size="lg" /><span class="sr-only">Supprimer l’accueil dérogatoire</span>
              </EbLinkButton>
            </li>
          </ul>
          <p v-else>Vous n'avez aucun accueil archivé.</p>
        </div>
        <div v-else>
          <EbLoader></EbLoader>
        </div>
        
        <EbHeading :level="!home ? 'h2': 'h3'" class="eb-box-heading">Mes accueils archivés</EbHeading>
        <div v-if="!loading">
          <ul v-if="archived && archived.length > 0" class="eb-list">
            <li v-for="acc in archived" class="eb-list-item">
              <span class="eb-list-item-content" :class="{w75: !home} && {w50: !isMobile}">
                <EbEtiquette v-if="home || isMobile" type="archive">Archive</EbEtiquette>
                <p class="eb-list-item-title">
                  <span class="text-uppercase ">{{ acc.nom }}</span> {{ acc.prenom }}
                </p>
                <p>
                  Date de départ : <b><time :datetime="acc.dateDepart">{{ getLongDate(acc.dateDepart) }}</time></b>
                </p>
                <EbEtiquette v-if="home || isMobile" type="archive" lightBg>Déclaration modifiée le <time :datetime="acc.dateModification">{{ getLongDate(acc.dateModification) }}</time></EbEtiquette>
              </span>
              <EbEtiquette v-if="!home && !isMobile" type="archive" lightBg>Déclaration modifiée le <time :datetime="acc.dateModification">{{ getLongDate(acc.dateModification) }}</time></EbEtiquette>
              <EbEtiquette v-if="!home && !isMobile" type="archive">Archive</EbEtiquette>
            </li>
          </ul>
          <p v-else>Vous n'avez aucun accueil archivé.</p>
        </div>
        <div v-else>
          <EbLoader></EbLoader>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import helpers from '../mixins/helpers'

export default {
  name: 'mesAccueils',
  mixins: [helpers],
  props: {
    home: Boolean,
    status: {
      type: String,
      default: ''
    }		
  },
  data() {
    return {
      enCours: [],
      archived: [],
      derogatoire: [],
      loading: true,
      error: false,
      isDeclarationSaved: false,
      declarationStatusMessage: '',
      publicProfileUrl: '',
      
      isBannerActive: false,
      bannerTimestamp: '',
      bannerType: null,
      bannerTitle: '',
      bannerContent: ''		
    }
  },
  mounted() {
    this.getAccueils()
    this.getAccueilsDerogatoires()
    this.publicProfileUrl = this.getPublicProfileLink()
    this.bannerTitle = null
    this.bannerTimestamp = this.getTime()
    
    if(this.status !== '') {
      this.isBannerActive = true
      this.bannerType = "success"
      this.bannerTimestamp = this.getTime()
      switch (this.status) {
        case 'create':
        this.bannerContent = 'L’enregistrement de la déclaration d’accueil a bien été pris en compte.'
        break;
        case 'update':
        this.bannerContent = 'La modification de la déclaration d’accueil a bien été prise en compte.'
        break;
        case 'delete':
        this.bannerContent = 'La déclaration de fin d’accueil a bien été prise en compte.'
        break;
        case 'deleteDerogatoire':
        this.bannerContent = 'La suppression de l’accueil dérogatoire a bien été prise en compte, un message a été envoyé à votre PMI de rattachement.'
        break;
      }
    }		
  },
  methods: {
    getAccueils: async function() {
      this.isBannerActive = false
      
      await this.$http.get('assmat/enfants')
      .then(resp => {
        this.enCours = resp.data.accueilsEnfantEnCours
        this.archived = resp.data.accueilsEnfantFinis
        this.loading = false
      })
      .catch(error => {
        this.loading = false
        
        this.isBannerActive = true
        this.bannerType = "error"
        this.bannerTitle = "Erreur lors de la récupération des données"
        this.bannerContent = 'La déclaration de fin d’accueil a bien été prise en compte.'
        this.bannerTimestamp = this.getTime()
      })
    },
    getTitleLinkFiche: function(e) {
      return `Voir la fiche de ${e.nom} ${e.prenom}`
    },
    getAccueilsDerogatoires: async function() {
      await this.$http.get('assmat/accueilsDerogatoires')
      .then(resp => {
        this.derogatoire = resp.data
      })
      .catch(error => {
        this.isBannerActive = true
        this.bannerType = "error"
        this.bannerTitle = "Erreur lors de la récupération des données"
        this.bannerContent = 'La déclaration de fin d’accueil a bien été prise en compte.'
        this.bannerTimestamp = this.getTime()
      })
    }
  },
}
</script>
